import clsx from 'clsx'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Close, Search } from '@/components-latest/primitives/Icons'
import Input from '@/components-latest/primitives/Input'
import Popover, {
  PopoverContent,
  PopoverTrigger
} from '@/components-latest/primitives/Popover/Popover'
import Spinner from '@/components-latest/primitives/Spinner'
import Tag from '@/components-latest/primitives/Tag'
import Text, { textVariants } from '@/components-latest/primitives/Text'

import Styles from './OrdersSearchInput.module.scss'

export type IOrderHistorySearchCategory = 'ITEM' | 'ORDER_ID' | 'PERSON'

type Props = {
  className?: string
  id: string | number
  value: string
  category: IOrderHistorySearchCategory | ''
  onChange: (value: string, category: IOrderHistorySearchCategory | '') => void
  onSubmit: (value: string, category: IOrderHistorySearchCategory) => void
  isLoading?: boolean
}

const OrdersSearchInput = React.memo(
  ({
    className,
    id,
    category,
    value,
    onChange,
    onSubmit,
    isLoading
  }: Props) => {
    const { t } = useTranslation()

    const inputLeftSideRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    const [isOpen, setIsOpen] = useState(false)
    const openPopover = () => {
      setIsOpen(true)
    }
    const closePopover = useCallback(() => {
      setIsOpen(false)
    }, [])

    useEffect(() => {
      if (isLoading) closePopover()
    }, [isLoading, closePopover])

    const minLengthRequiredForPopover = 2

    const isSearchLengthCorrect = value.length >= minLengthRequiredForPopover

    const categories: Array<{ id: IOrderHistorySearchCategory; name: string }> =
      useMemo(
        () => [
          { id: 'PERSON', name: t('orders.search-input.person') },
          { id: 'ORDER_ID', name: t('orders.search-input.order-id') },
          { id: 'ITEM', name: t('orders.search-input.item') }
        ],
        [t]
      )
    const activeCategory = categories.find(({ id }) => category === id)

    useEffect(() => {
      if (inputLeftSideRef.current && inputRef.current) {
        const elem = inputLeftSideRef.current
        inputRef.current.style.setProperty(
          '--search-input-padding-left',
          `${elem.clientWidth}px`
        )
      }
    }, [category])

    const onInputChangeEnhanced = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        const isValueLengthWrong = value.length < minLengthRequiredForPopover

        onChange(value, isValueLengthWrong ? '' : category)
      },
      [onChange, category, minLengthRequiredForPopover]
    )

    const onEnterPress = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') return

        if (isSearchLengthCorrect) {
          onChange(value, category || 'PERSON')
          onSubmit(value, category || 'PERSON')
        }
        if (value.length === 0) clearValues()
        closePopover()
      },
      [onSubmit, value, category, isSearchLengthCorrect, onChange, closePopover]
    )

    const onOptionClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.currentTarget
        const categoryId = target.dataset
          .categoryId as IOrderHistorySearchCategory

        onChange(value, categoryId)
        onSubmit(value, categoryId)
        closePopover()
      },
      [value, onChange, onSubmit, closePopover]
    )

    const clearValues = useCallback(() => {
      onChange('', '')
      onSubmit('', 'PERSON')
    }, [onSubmit, onChange])

    return (
      <Popover open={isOpen}>
        <PopoverTrigger className={clsx(className, Styles.trigger)}>
          <div ref={inputLeftSideRef} className={Styles.inputLeftSide}>
            <Search
              onClick={() => {
                if (isSearchLengthCorrect) {
                  onChange(value, category || 'PERSON')
                  onSubmit(value, category || 'PERSON')
                }
                if (value.length === 0) clearValues()
              }}
            />
            {category && (
              <Tag
                className={Styles.tag}
                data-testid="main-orders-search-tag"
                id={`${id}-orders-search-tag`}
                intent="secondary"
                variant="fill"
                size="medium">
                <p
                  className={textVariants({
                    variant: 'nextBody',
                    weight: 'semiBold'
                  })}>
                  {activeCategory?.name}
                </p>
              </Tag>
            )}
          </div>
          <Input
            onChange={onInputChangeEnhanced}
            onKeyPress={onEnterPress}
            ref={inputRef}
            value={value}
            data-testid="orders-search-input"
            type="text"
            autoComplete="off"
            data-search-input-id={String(id)}
            onFocus={openPopover}
            disabled={isLoading}
            placeholder={t('orders.search-placeholder')}
          />
          {value && (
            <Close onClick={clearValues} className={Styles.closeIcon} />
          )}
          {isLoading && (
            <div className={Styles.loader}>
              <Spinner size="small" />
            </div>
          )}
        </PopoverTrigger>
        <PopoverContent
          className={Styles.content}
          onPointerDownOutside={e => {
            const target = e.target as HTMLDivElement
            if (target.dataset?.searchInputId !== String(id)) {
              closePopover()
            }
          }}
          onOpenAutoFocus={e => e.preventDefault()}>
          <div data-search-content={true}>
            {isSearchLengthCorrect ? (
              categories.map(({ id, name }, index) => (
                <div
                  className={clsx(Styles.option, {
                    [Styles.borderNone]: index + 1 === categories.length,
                    [Styles.active]: category === id
                  })}
                  onClick={onOptionClick}
                  data-category-id={id}
                  key={id}>
                  <Search />
                  <Text
                    variant="nextBody"
                    title={value}
                    className={Styles.optionText}
                    asChild>
                    <p>{value}</p>
                  </Text>
                  <Tag
                    className={clsx(Styles.tag, Styles.contentTag)}
                    data-testid={`option-orders-search-tag-${id}`}
                    id={`${id}-orders-search-tag`}
                    intent="secondary"
                    variant="fill"
                    size="medium">
                    <p>{name}</p>
                  </Tag>
                </div>
              ))
            ) : (
              <div className={Styles.empty}>
                <img src="/images/emptyHistorySearch.svg" alt="empty-search" />
                <Text asChild variant="title-3" weight="semiBold">
                  <p>{t('orders.search-input.empty-title')}</p>
                </Text>
                <Text asChild variant="subtitle">
                  <p>{t('orders.search-input.empty-subtitle')}</p>
                </Text>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    )
  }
)

OrdersSearchInput.displayName = 'OrdersSearchInput'
export default OrdersSearchInput
